import { numberWithCommas } from '@common/utils';
import {
  formatKoreanNumber,
  GetPaymentNotificationList_payment_notification_list_item_list_payment_notification_content_list as payment_notification_content_list,
  VendorEventType,
} from '@domains/pdp-common';

import { PaymentEventItem } from '../services/useBenefitService';
import { Benefit, Benefits } from '../types/benefit';
import { getMinimumOrderText } from './getMinimumOrderText';
import { getPaymentBenefitText } from './getPaymentBenefitText';

type Coupon = {
  targetRef: (instance: Element | null) => void;
  discountAmount: number;
  title: string;
  issue_status: string;
  checked: boolean;
  onChecked: (checked: boolean) => void;
  buttonOnClick: () => void;
};

interface BenefitsCoupon {
  displayMaxCoupon: boolean;
  buttonColor: string;
  coupon: Coupon;
  duplication_coupon: Coupon;
}

interface BenefitsPoint {
  point: {
    usePoint: number;
    currentPoint: number;
    shouldNotifyMinPoint: boolean;
    shouldNotifyPointUsable: boolean;
  };

  disabled: boolean;
  checked: boolean;
  onChecked: (checked: boolean) => void;
}

interface BenefitPayment {
  filterPaymentEventList: PaymentEventItem[];
  checkedList: boolean[];
  onChecked: (checked: boolean, id: number) => void;
}

type MileageInfo = {
  current: number;
  use: number;
  isUsable: boolean;
  moreThanMin: boolean;
};

type ShopInfo = {
  name: string;
  isMileageUsable: boolean;
  addRate: number;
  defaultRate: number;
};

interface BenefitMileage {
  mileage: MileageInfo;
  shop: ShopInfo;
  checked: boolean;
  onChecked: (checked: boolean) => void;
}

export const generateBenefitInfoUtils = () => {
  const getDefaultBenefitInfo = (
    storeDiscountAmount: number,
    promotionDiscountAmount: number,
    bogoPromotionDiscountAmount: number,
  ): Benefits => {
    const totalAmount = storeDiscountAmount + promotionDiscountAmount + bogoPromotionDiscountAmount;
    if (!totalAmount) {
      return [];
    }
    const benefit: Benefit = {
      type: 'checkbox',
      title: '상품 할인',
      totalAmount: `-${numberWithCommas(totalAmount)}원`,
      disabled: true,
      checked: true,
      items: [],
    };

    if (storeDiscountAmount) {
      benefit.items.push({
        type: 'normal',
        content: `스토어 할인 -${numberWithCommas(storeDiscountAmount)}원`,
      });
    }

    if (promotionDiscountAmount) {
      benefit.items.push({
        type: 'normal',
        content: `지그재그 추가 할인 -${numberWithCommas(promotionDiscountAmount)}원`,
      });
    }

    if (bogoPromotionDiscountAmount) {
      benefit.items.push({
        type: 'normal',
        content: `1+1 추가 할인 -${bogoPromotionDiscountAmount}원`,
      });
    }
    return [benefit];
  };

  const getFirstOrderBenefitInfo = (firstOrderDiscountAmount: number, buttonOnClick: () => void): Benefits => {
    if (!firstOrderDiscountAmount) {
      return [];
    }

    return [
      {
        type: 'checkbox',
        title: '첫구매 할인',
        totalAmount: `-${numberWithCommas(firstOrderDiscountAmount)}원`,
        disabled: true,
        checked: true,
        items: [
          {
            type: 'normal',
            content: '회원가입 후 첫 구매 시 적용',
            button: {
              type: 'signup',
              text: '회원가입',
              onClick: buttonOnClick,
            },
          },
        ],
      },
    ];
  };

  const getCouponBenefitInfo = ({
    displayMaxCoupon,
    buttonColor,
    coupon,
    duplication_coupon,
  }: BenefitsCoupon): Benefits => {
    if (!displayMaxCoupon) {
      return [];
    }

    const benefit: Benefit[] = [];

    if (coupon.discountAmount) {
      const issuable = coupon.issue_status === 'ISSUABLE';
      benefit.push({
        ref: coupon.targetRef,
        type: 'checkbox',
        title: '쿠폰 할인',
        totalAmount: `-${numberWithCommas(coupon.discountAmount)}원`,
        disabled: false,
        checked: coupon.checked,
        onChecked: coupon.onChecked,
        items: [
          {
            type: 'normal',
            content: coupon.title,
            button: {
              type: 'download',
              disable: !issuable,
              text: issuable ? '받기' : '보유',
              uiSetting: {
                color: buttonColor,
              },
              onClick: () => coupon.buttonOnClick(),
            },
          },
        ],
      });
    }

    if (duplication_coupon.discountAmount) {
      const issuable = duplication_coupon.issue_status === 'ISSUABLE';

      benefit.push({
        ref: duplication_coupon.targetRef,
        type: 'checkbox',
        title: `${benefit.length ? '중복 ' : ''}쿠폰 할인`,
        totalAmount: `-${numberWithCommas(duplication_coupon.discountAmount)}원`,
        disabled: false,
        checked: duplication_coupon.checked,
        onChecked: duplication_coupon.onChecked,
        items: [
          {
            type: 'normal',
            content: duplication_coupon.title,
            button: {
              type: 'download',
              disable: !issuable,
              text: issuable ? '받기' : '보유',
              uiSetting: {
                color: buttonColor,
              },
              onClick: () => duplication_coupon.buttonOnClick(),
            },
          },
        ],
      });
    }
    return benefit;
  };

  const getPointBenefitInfo = ({
    point: { usePoint, currentPoint, shouldNotifyMinPoint, shouldNotifyPointUsable },
    disabled,
    checked,
    onChecked,
  }: BenefitsPoint): Benefits => {
    if (!currentPoint) {
      return [];
    }
    const benefit: Benefit = {
      type: 'checkbox',
      title: '보유 포인트',
      subTitle: `${numberWithCommas(currentPoint)}P`,
      totalAmount: disabled ? '-' : `-${numberWithCommas(usePoint)}원`,
      disabled,
      checked,
      onChecked: onChecked,
      items: [],
    };

    if (shouldNotifyMinPoint) {
      benefit.items.push({
        type: 'notify',
        content: '포인트는 100P 이상 보유 시 사용 가능해요',
      });
    } else if (shouldNotifyPointUsable) {
      benefit.items.push({
        type: 'notify',
        content: '포인트는 최소 100P부터 사용 가능해요',
      });
    }

    return [benefit];
  };

  const getMileageBenefitInfo = ({ mileage, shop, checked, onChecked }: BenefitMileage) => {
    if (!mileage.current) {
      return [];
    }

    const disabled = !shop.isMileageUsable || !mileage.isUsable;

    const benefit: Benefit = {
      type: 'checkbox',
      title: '보유 마일리지',
      subTitle: `${numberWithCommas(mileage.current)}M`,
      totalAmount: disabled ? '-' : `-${numberWithCommas(mileage.use)}원`,
      disabled,
      checked,
      onChecked,
      items: [],
    };

    if (!shop.isMileageUsable) {
      benefit.items.push({
        type: 'notify',
        content: `이 상품은 마일리지 사용이 불가해요`,
      });
    } else if (!mileage.moreThanMin) {
      benefit.items.push({
        type: 'notify',
        content: `마일리지는 1,000M 이상 보유 시 사용 가능해요`,
      });
    } else if (!mileage.isUsable) {
      benefit.items.push({
        type: 'notify',
        content: `마일리지는 최소 100M부터 사용 가능해요`,
      });
    } else if (!disabled) {
      const addContent = shop.addRate ? `+ ${shop.name} ${shop.addRate}%` : '';
      benefit.items.push({
        type: 'normal',
        content: `사용한도: 기본 ${shop.defaultRate}% ${addContent}`,
      });
    }

    return [benefit];
  };

  const getPaymentEventBenefitInfo = ({ filterPaymentEventList, checkedList, onChecked }: BenefitPayment): Benefits => {
    if (!filterPaymentEventList.length) {
      return [];
    }

    const benefit: Benefit = {
      type: 'normal',
      title: '추가 결제혜택',
      items: [],
    };

    benefit.items = filterPaymentEventList.map((item) => {
      const card_name = getPaymentBenefitText({
        language: 'ko',
        payment_method: item.payment_method,
        payment_method_type: item.payment_method_type,
        financial_company_code: item.financial_company_code as string,
        payment_notification_content_list:
          item.payment_notification_content_list as payment_notification_content_list[],
      });

      const discount_text = item.event_type === VendorEventType.CARD_CLAIM ? '청구할인' : '즉시할인';

      const immediate_discount_text = `${formatKoreanNumber(item.discount_amount)} ${discount_text}`;

      let content = `${card_name} ${immediate_discount_text} ${getMinimumOrderText(item.min_amount)}`;

      if (item.payment_notification_content_list) {
        content = card_name;
      }

      return {
        type: 'checkbox',
        content,
        subContent: `-${numberWithCommas(item.discount_amount)}원`,
        checked: checkedList[item.id],
        onChecked: (checked) => onChecked(checked, item.id),
      };
    });

    return [benefit];
  };

  return {
    getDefaultBenefitInfo,
    getFirstOrderBenefitInfo,
    getCouponBenefitInfo,
    getPointBenefitInfo,
    getMileageBenefitInfo,
    getPaymentEventBenefitInfo,
  };
};
